import { Outlet, useParams, useSearchParams } from "react-router-dom";
import { Affix, Spin, Input } from "antd";
import { Icon } from "@terminus/dqh-web-ui";
import { useEffect, useState } from "react";
import { Header } from "./head";
import { Footer } from "./footer";
import { getProjectDetail } from "@/services/business";
import styles from "./project-detail-layout.module.scss";

type ProjectDetail = {
  projectKey: string;
  projectName?: string;
  buyerName?: string;
  province?: string;
  city?: string;
  district?: string;
  address?: string;
  projectCategory?: string;
  projectNature?: string;
  industryType?: string;
  totalInvestmentWan?: number;
  budgetAmountWan?: number;
  constructionStartAt?: number;
  constructionEndAt?: number;
  constructionPeriod?: string;
  summary?: string;
  latestNoticeAt?: number;
  subscribed?: boolean;
};

const industryLabels: Record<string, string> = {
  MANUFACTURING: "工程制造",
  CONSTRUCTION: "工程建造",
  ENERGY_CHEMICAL: "能源行业",
  WHOLESALE_RETAIL: "零售批发",
  PUBLIC_ADMINISTRATION: "政企单位",
  INFORMATION_TECHNOLOGY: "信息技术",
  TRANSPORTATION_LOGISTICS: "交通运输",
  FINANCE: "金融",
  REAL_ESTATE: "房地产",
  AGRICULTURE: "农林牧渔",
  PROFESSIONAL_SERVICES: "专业服务",
  HOSPITALITY_TOURISM: "住宿餐饮",
  CULTURE_SPORTS: "文化体育",
  ENVIRONMENTAL_SERVICES: "环保服务",
  OTHER: "其他",
};

const formatAmount = (amount?: number) => {
  if (amount == null) return "-";
  return amount >= 10_000 ? `${amount / 10_000}亿以上` : `${amount}万以上`;
};

const formatDate = (timestamp?: number) =>
  timestamp
    ? new Date(timestamp).toLocaleDateString("zh-CN").replaceAll("/", "-")
    : "-";

const formatPeriod = (detail?: ProjectDetail) => {
  if (!detail) return "-";
  if (detail.constructionStartAt && detail.constructionEndAt) {
    return `${formatDate(detail.constructionStartAt)} 至 ${formatDate(detail.constructionEndAt)}`;
  }
  return detail.constructionPeriod || "-";
};

export const ProductDetailLayout = () => {
  const { projectKey } = useParams<{ projectKey: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projectDetail, setProjectDetail] = useState<ProjectDetail>();
  const [loading, setLoading] = useState(false);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  // 当前激活 tab 由 URL 参数驱动，与下方子页面 ProjectDCompanyPortalPage 共享
  const activeTab = searchParams.get("tab") || "latest";
  const [searchValue, setSearchValue] = useState("");

  // 切换 tab 时，把搜索框回填为该 tab 当前已生效的关键词
  // biome-ignore lint/correctness/useExhaustiveDependencies: 仅在 tab 切换时同步搜索框显示值
  useEffect(() => {
    setSearchValue(
      activeTab === "notice"
        ? searchParams.get("query") || ""
        : searchParams.get("keyword") || "",
    );
  }, [activeTab]);

  // 按当前 tab 把关键词写入对应 URL 参数：商机页读 keyword，公示页读 query
  const handleHeaderSearch = (value: string) => {
    const keyword = value.trim();
    const params = new URLSearchParams(searchParams);
    if (activeTab === "notice") {
      params.set("query", keyword);
    } else {
      params.set("keyword", keyword);
    }
    // preventScrollReset 阻止本次搜索导航把页面滚动到顶部
    setSearchParams(params, { preventScrollReset: true });
  };

  useEffect(() => {
    if (!projectKey) return;
    setLoading(true);
    getProjectDetail(projectKey)
      .then((response: any) => setProjectDetail(response?.data || response))
      .finally(() => setLoading(false));
  }, [projectKey]);

  const projectLocation = [projectDetail?.province, projectDetail?.city]
    .filter(Boolean)
    .join("");
  const projectAddress =
    projectDetail?.address ||
    [projectDetail?.province, projectDetail?.city, projectDetail?.district]
      .filter(Boolean)
      .join("");
  const investment =
    projectDetail?.totalInvestmentWan ?? projectDetail?.budgetAmountWan;

  const infoItems = [
    { icon: "bendilocal-two", label: "项目地区", value: projectLocation || "-" },
    { icon: "shijiantime", label: "总投资额", value: formatAmount(investment) },
    {
      icon: "jinrongfinance",
      label: "行业类型",
      value: industryLabels[projectDetail?.industryType || ""] || "-",
    },
    {
      icon: "bendilocal-two",
      label: "项目类别",
      value: projectDetail?.projectCategory || "-",
    },
    {
      icon: "shijiantime",
      label: "项目性质",
      value: projectDetail?.projectNature || "-",
    },
    { icon: "shijiantime", label: "建设周期", value: formatPeriod(projectDetail) },
  ];

  return (
    <div className={styles.globalWrapper}>
      <Affix>
        <Header noTabs />
      </Affix>
      <Spin spinning={loading}>
        {/* 固定条：整宽白底，logo + 竖线 + 标题行一起固定 */}
        <Affix offsetTop={44}>
          <section className={styles.stickyBar}>
            <div className={styles.stickyBarInner}>
              <img
                className={styles.logo}
                src="https://public-daqihui-prod.oss-cn-hangzhou.aliyuncs.com/fe-resources/cxfe/statics/img/daqihui-pc/logo.png"
                alt="大企汇"
              />
              <div className={styles.divider} />
              <div className={styles.titleRow}>
                <div className={styles.titleContent}>
                  <h1 className={styles.title}>
                    {projectDetail?.projectName || "项目详情"}
                  </h1>
                  <div className={styles.metaRow}>
                    <span className={styles.buyer}>
                      <Icon name="shangquanshopping-mall" size={14} color="#777779" />
                      {projectDetail?.buyerName || "-"}
                    </span>
                    <button type="button" className={styles.subscribe}>
                      <Icon
                        name="shangjikapian-dingyueqiye"
                        size={14}
                        color="#ff5600"
                      />
                      订阅
                    </button>
                  </div>
                </div>
                <div className={styles.actions}>
                  <Input.Search
                    className={styles.headerSearch}
                    value={searchValue}
                    allowClear
                    size="large"
                    enterButton="搜索"
                    placeholder={
                      activeTab === "notice"
                        ? "搜索公示 标题、内容"
                        : "搜索商机 标题、内容"
                    }
                    onChange={(e) => setSearchValue(e.target.value)}
                    onSearch={handleHeaderSearch}
                  />
                  <button type="button" className={styles.ghostBtn}>
                    <Icon name="goutongcommunication" size={16} color="#ff5600" />
                    查看来源
                  </button>
                </div>
              </div>
            </div>
          </section>
        </Affix>

        {/* 详情内容：不固定，与标题行左对齐 */}
        <section className={styles.detailSection}>
          <div className={styles.detailInner}>
            <div className={styles.infoGrid}>
              {infoItems.map((item) => (
                <div className={styles.infoItem} key={item.label}>
                  <span className={styles.label}>
                    <Icon name={item.icon} size={14} color="#777779" />
                    {item.label}
                  </span>
                  <strong className={styles.value}>{item.value}</strong>
                </div>
              ))}
            </div>

            <div className={styles.detailLine}>
              <span className={styles.label}>
                <Icon name="bendilocal-two" size={14} color="#777779" />
                项目地址：
              </span>
              <span className={styles.detailValue}>{projectAddress || "-"}</span>
            </div>

            <div className={styles.summaryRow}>
              <span className={`${styles.label} ${styles.summaryLabel}`}>
                <Icon name="bendilocal-two" size={14} color="#777779" />
                项目说明：
              </span>
              <p
                className={`${styles.summaryText} ${summaryExpanded ? "" : styles.summaryClamp}`}
              >
                {projectDetail?.summary || "暂无项目说明"}
              </p>
              {projectDetail?.summary && (
                <button
                  type="button"
                  className={styles.expandBtn}
                  onClick={() => setSummaryExpanded((prev) => !prev)}
                >
                  {summaryExpanded ? "收起" : "展开"}
                  <Icon
                    name={summaryExpanded ? "shangup" : "xiadown"}
                    size={12}
                    color="#ff5600"
                  />
                </button>
              )}
            </div>

            <div className={styles.updateTime}>
              <Icon name="shijiantime" size={14} color="#777779" />
              更新时间：{formatDate(projectDetail?.latestNoticeAt)} 发布
            </div>
          </div>
        </section>
        <div className={styles.outLayoutWrapper}>
          <div className={styles.layoutWrapper}>
            <div className={styles.layoutContent}>
              <Outlet context={{ projectKey, projectDetail }} />
            </div>
          </div>
        </div>
      </Spin>
      <div style={{ width: 1200, margin: "0 auto" }}>
        <Footer />
      </div>
    </div>
  );
};
